import React, { useState, useEffect, memo } from 'react';

enum IconName {
	approvedCircle = 'approved-circle',
	archive = 'archive',
	archiveLarge = 'archive-large',
	arrowDropDown = 'arrow-drop-down',
	arrowLeft = 'arrow-left',
	adminSettings = 'adminSettings',
	checkboxChecked = 'checkbox-checked',
	checkboxUnchecked = 'checkbox-unchecked',
	chevronDown = 'chevron-down',
	chevronLeft = 'chevron-left',
	chevronRight = 'chevron-right',
	chevronUp = 'chevron-up',
	clientTypeAll = 'client-type-all',
	clientTypeFastFood = 'client-type-fast-food',
	clientTypeHotels = 'client-type-hotels',
	clientTypeRestaurants = 'client-type-restaurants',
	close = 'close',
	credentials = 'credentials',
	customersAndRestaurants = 'customersAndRestaurants',
	delete = 'delete',
	deleteLarge = 'delete-large',
	devices = 'devices',
	dollar = 'dollar',
	done = 'done',
	download = 'download',
	edit = 'edit',
	empty = 'empty',
	emptyLarge = 'empty-large',
	entertainment = 'entertainment',
	errorCircle = 'error-circle',
	errorLarge = 'error-large',
	eye = 'eye',
	eyeOff = 'eye-off',
	fileCopy = 'file-copy',
	file = 'file',
	filter = 'filter',
	firmwareAndApp = 'firmwareAndApp',
	image = 'image',
	info = 'info',
	infoCircle = 'info-circle',
	loaderSpinner = 'loader-spinner',
	menu = 'menu',
	menuV2 = 'menu-v2',
	moodBad = 'mood-bad',
	plus = 'plus',
	refresh = 'refresh',
	restore = 'restore',
	restoreLarge = 'restore-large',
	saveLarge = 'save-large',
	search = 'search',
	success = 'success',
	teasers = 'teasers',
	upload = 'upload',
	uploadOutlined = 'upload-outlined',
	user = 'user',
	users = 'users',
	watches = 'watches',
}

const sizeMap = {
	sm: '10px',
	md: '16px',
	lg: '24px',
	xl: '32px',
	xxl: '40px',
	xxxl: '64px',
	smallHero: '128px',
	hero: '184px',
};

interface IProps {
	name: IconName;
	size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | 'smallHero' | 'hero';
	className?: string;
	onClick?: () => void;
}

export const Icon: React.FC<IProps> = memo(({ name, size = 'md', className, onClick }) => {
	const [SvgComponent, setSvgComponent] = useState<React.FC<React.SVGProps<SVGSVGElement>> | null>(null);

	useEffect(() => {
		let isMounted = true;

		const loadIcon = async () => {
			const module = await import(`./svg/${name}.tsx`);
			if (!isMounted) {
				return;
			}
			setSvgComponent(() => module.default);
		};

		loadIcon();

		return () => {
			isMounted = false;
		};
	}, [name]);

	if (!SvgComponent) {
		return (
			<svg
				viewBox="0 0 10 10"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				className={className}
				width={sizeMap[size]}
				height={sizeMap[size]}
				onClick={onClick}
			/>
		);
	}

	return (
		<SvgComponent
			className={className}
			width={sizeMap[size]}
			height={sizeMap[size]}
			aria-hidden="true"
			role="img"
			onClick={onClick}
		/>
	);
});

export { IconName };
